import { API_PATH, BASE_PATH } from './config';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Chip,
    TextField,
    InputAdornment,
    Grid,
    Card,
    CardContent,
    Collapse,
    Divider,
    useMediaQuery,
    useTheme
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import LoginIcon from '@mui/icons-material/Login';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';

// Format an absolute datetime string for display (local time)
const fmtDate = (s) => {
    if (!s) return '—';
    const d = new Date((s || '').replace(' ', 'T'));
    return isNaN(d) ? '—' : d.toLocaleDateString();
};

// Compact "time ago" for last-activity columns
const timeAgo = (s) => {
    if (!s) return 'Never';
    const d = new Date((s || '').replace(' ', 'T'));
    if (isNaN(d)) return '—';
    const secs = Math.floor((Date.now() - d.getTime()) / 1000);
    if (secs < 60) return 'Just now';
    const mins = Math.floor(secs / 60);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months}mo ago`;
    return `${Math.floor(months / 12)}y ago`;
};

// Dark admin theme tokens
const dk = {
    bg: '#0d1117',
    card: '#161b22',
    elevated: '#21262d',
    hover: '#2d333b',
    border: 'rgba(99, 102, 241, 0.2)',
    borderHover: 'rgba(99, 102, 241, 0.4)',
    textPrimary: '#e6edf3',
    textSecondary: '#8b949e',
    textMuted: '#6e7681',
    accent: '#6366f1',
    accentLight: '#818cf8',
};

const UserManagement = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    const [userTripsDialogOpen, setUserTripsDialogOpen] = useState(false);
    const [userTrips, setUserTrips] = useState([]);
    const [loadingTrips, setLoadingTrips] = useState(false);
    const [tripsError, setTripsError] = useState(null);
    // Trip drill-down (inline details) state
    const [expandedTripId, setExpandedTripId] = useState(null);
    const [tripDetails, setTripDetails] = useState({}); // trip_id -> details|'loading'|'error'

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        setLoading(true);
        setError(null);

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_PATH}/admin/get-users.php`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.status === 403) {
                setError('Access denied. Admin only.');
                return;
            }

            if (response.status === 401) {
                setError('Please log in to access this page.');
                return;
            }

            const result = await response.json();

            if (result.success) {
                setUsers(result.users);
            } else {
                setError(result.error || 'Failed to fetch users');
            }
        } catch (err) {
            setError('Failed to connect to server: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleViewTrips = async (user) => {
        setSelectedUser(user);
        setUserTripsDialogOpen(true);
        setLoadingTrips(true);
        setTripsError(null);
        setExpandedTripId(null);
        setTripDetails({});

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_PATH}/admin/get-user-trips.php?pu_id=${user.pu_id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            const result = await response.json();

            if (result.success) {
                setUserTrips(result.trips);
            } else {
                setTripsError(result.error || 'Failed to fetch trips');
            }
        } catch (err) {
            setTripsError('Failed to connect to server: ' + err.message);
        } finally {
            setLoadingTrips(false);
        }
    };

    const handleToggleTripDetails = async (trip) => {
        const id = trip.trip_id;
        if (expandedTripId === id) { setExpandedTripId(null); return; }
        setExpandedTripId(id);

        // Fetch once, then cache
        if (tripDetails[id] && tripDetails[id] !== 'error') return;
        setTripDetails(prev => ({ ...prev, [id]: 'loading' }));
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_PATH}/admin/get-trip-details.php?trip_id=${id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const result = await response.json();
            if (result.success) {
                setTripDetails(prev => ({ ...prev, [id]: result.trip }));
            } else {
                setTripDetails(prev => ({ ...prev, [id]: 'error' }));
            }
        } catch (err) {
            setTripDetails(prev => ({ ...prev, [id]: 'error' }));
        }
    };

    const handleAccessTrip = async (trip) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_PATH}/admin/access-trip.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    trip_id: trip.trip_id,
                    pu_id: selectedUser.pu_id
                })
            });

            const result = await response.json();

            if (result.success) {
                // Use React Router navigation instead of window.location.href
                console.log('Navigating to view-trip:', trip.trip_id);
                navigate(`/view-trip/${trip.trip_id}`);
            } else {
                setTripsError(result.error || 'Failed to access trip');
            }
        } catch (err) {
            setTripsError('Failed to access trip: ' + err.message);
        }
    };

    const filteredUsers = users.filter(user =>
        user.display_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.phone_number?.includes(searchTerm)
    );

    return (
        <Box sx={{ bgcolor: dk.bg, minHeight: '100vh', pt: 1 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
                <Typography variant="h6" component="h2" sx={{ flexGrow: 1, color: dk.textPrimary, fontWeight: 700 }}>
                    User Management
                </Typography>
                <Button
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : <RefreshIcon />}
                    onClick={fetchUsers}
                    disabled={loading}
                    sx={{ bgcolor: dk.accent, '&:hover': { bgcolor: '#4f46e5' }, '&.Mui-disabled': { bgcolor: dk.elevated, color: dk.textMuted } }}
                >
                    Refresh
                </Button>
            </Box>

            {/* Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ bgcolor: dk.card, border: `1px solid ${dk.border}`, borderRadius: 2, boxShadow: 'none' }}>
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                            <Typography variant="h4" sx={{ color: dk.accentLight, fontWeight: 700 }}>{users.length}</Typography>
                            <Typography variant="body2" sx={{ color: dk.textSecondary }}>Total Users (Non-Guest)</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ bgcolor: dk.card, border: '1px solid rgba(16,185,129,0.35)', borderRadius: 2, boxShadow: 'none' }}>
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                            <Typography variant="h4" sx={{ color: '#10b981', fontWeight: 700 }}>{users.filter(u => u.verified).length}</Typography>
                            <Typography variant="body2" sx={{ color: dk.textSecondary }}>Verified Users</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ bgcolor: dk.card, border: `1px solid ${dk.border}`, borderRadius: 2, boxShadow: 'none' }}>
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                            <Typography variant="h4" sx={{ color: '#f59e0b', fontWeight: 700 }}>{users.reduce((s, u) => s + (u.trip_count || 0), 0)}</Typography>
                            <Typography variant="body2" sx={{ color: dk.textSecondary }}>Total Trips</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ bgcolor: dk.card, border: `1px solid ${dk.border}`, borderRadius: 2, boxShadow: 'none' }}>
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                            <Typography variant="h4" sx={{ color: '#06b6d4', fontWeight: 700 }}>
                                {users.filter(u => u.last_seen && (Date.now() - new Date((u.last_seen || '').replace(' ', 'T')).getTime()) < 7 * 864e5).length}
                            </Typography>
                            <Typography variant="body2" sx={{ color: dk.textSecondary }}>Active (7 days)</Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Search */}
            <TextField
                fullWidth
                placeholder="Search by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon sx={{ color: dk.textMuted }} />
                        </InputAdornment>
                    )
                }}
                sx={{
                    mb: 3,
                    '& .MuiInputLabel-root': { color: dk.textSecondary },
                    '& .MuiInputBase-input': { color: dk.textPrimary },
                    '& .MuiInputBase-input::placeholder': { color: dk.textMuted, opacity: 1 },
                    '& .MuiOutlinedInput-root': {
                        bgcolor: dk.card,
                        '& fieldset': { borderColor: dk.border },
                        '&:hover fieldset': { borderColor: dk.borderHover },
                        '&.Mui-focused fieldset': { borderColor: dk.accent },
                    },
                }}
            />

            {/* Error */}
            {error && (
                <Alert severity="error" sx={{ mb: 3, bgcolor: 'rgba(239,68,68,0.1)', color: '#f87171', border: '1px solid rgba(239,68,68,0.3)', '& .MuiAlert-icon': { color: '#f87171' } }}>
                    {error}
                </Alert>
            )}

            {/* Loading */}
            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                    <CircularProgress sx={{ color: dk.accent }} />
                </Box>
            )}

            {/* Users Display - Table on Desktop, Cards on Mobile */}
            {!loading && (
                <>
                    {isMobile ? (
                        // Mobile Card View
                        <Grid container spacing={2}>
                            {filteredUsers.length === 0 ? (
                                <Grid item xs={12}>
                                    <Typography sx={{ color: dk.textSecondary, py: 5, textAlign: 'center' }}>
                                        No users found
                                    </Typography>
                                </Grid>
                            ) : (
                                filteredUsers.map((user) => (
                                    <Grid item xs={12} key={user.pu_id}>
                                        <Card sx={{ bgcolor: dk.card, border: `1px solid ${dk.border}`, borderRadius: 2, boxShadow: 'none' }}>
                                            <CardContent sx={{ pb: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                                    <PersonIcon sx={{ fontSize: 20, color: dk.accentLight }} />
                                                    <Typography variant="h6" sx={{ flexGrow: 1, color: dk.textPrimary, fontWeight: 700 }}>
                                                        {user.display_name}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ mb: 1.5 }}>
                                                    {user.username && (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                            <EmailIcon sx={{ fontSize: 18, color: dk.textMuted }} />
                                                            <Typography variant="body2" sx={{ wordBreak: 'break-all', color: dk.textSecondary }}>
                                                                {user.username}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                    {user.phone_number && (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                            <PhoneIcon sx={{ fontSize: 18, color: dk.textMuted }} />
                                                            <Typography variant="body2" sx={{ color: dk.textSecondary }}>
                                                                {user.phone_number}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Box>
                                                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                                                    <Chip
                                                        label={user.account_status}
                                                        size="small"
                                                        sx={user.account_status === 'Active'
                                                            ? { color: '#10b981', bgcolor: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.3)', fontWeight: 600 }
                                                            : { color: dk.textSecondary, bgcolor: dk.elevated, border: `1px solid ${dk.border}` }
                                                        }
                                                    />
                                                    <Chip
                                                        label={user.verified ? '✓ Verified' : 'Unverified'}
                                                        size="small"
                                                        sx={user.verified
                                                            ? { color: '#10b981', bgcolor: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.3)', fontWeight: 600 }
                                                            : { color: dk.textMuted, bgcolor: dk.elevated, border: `1px solid ${dk.border}` }
                                                        }
                                                    />
                                                    <Chip
                                                        label={`${user.trip_count ?? 0} trips`}
                                                        size="small"
                                                        icon={<FlightTakeoffIcon sx={{ fontSize: 14, color: `${dk.accentLight} !important` }} />}
                                                        sx={{ color: dk.textPrimary, bgcolor: dk.elevated, border: `1px solid ${dk.border}`, fontWeight: 600 }}
                                                    />
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                                    <AccessTimeIcon sx={{ fontSize: 16, color: dk.textMuted }} />
                                                    <Typography variant="caption" sx={{ color: dk.textSecondary }}>
                                                        Active {timeAgo(user.last_seen)} · Joined {fmtDate(user.created_at)}
                                                    </Typography>
                                                </Box>
                                                <Button
                                                    fullWidth
                                                    variant="contained"
                                                    startIcon={<LoginIcon />}
                                                    onClick={() => handleViewTrips(user)}
                                                    size="small"
                                                    sx={{ bgcolor: dk.accent, '&:hover': { bgcolor: '#4f46e5' } }}
                                                >
                                                    View Trips
                                                </Button>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))
                            )}
                        </Grid>
                    ) : (
                        // Desktop Table View
                        <TableContainer component={Paper} sx={{ bgcolor: dk.card, border: `1px solid ${dk.border}`, borderRadius: 2, boxShadow: 'none', overflow: 'hidden' }}>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ bgcolor: dk.elevated }}>
                                        {['Name', 'Email', 'Phone', 'Status', 'Trips', 'Last Active', 'Created', ''].map((h, i, arr) => (
                                            <TableCell key={i} align={i === arr.length - 1 ? 'right' : 'left'} sx={{ color: dk.textSecondary, borderBottom: `1px solid ${dk.border}`, fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredUsers.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={8} align="center" sx={{ py: 5, borderBottom: `1px solid ${dk.border}` }}>
                                                <Typography sx={{ color: dk.textSecondary }}>
                                                    No users found
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        filteredUsers.map((user) => (
                                            <TableRow key={user.pu_id} sx={{ '&:hover': { bgcolor: dk.hover }, '& td': { borderBottom: `1px solid ${dk.border}` } }}>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <PersonIcon sx={{ fontSize: 20, color: dk.accentLight }} />
                                                        <Typography sx={{ color: dk.textPrimary }}>{user.display_name}</Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    {user.username ? (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <EmailIcon sx={{ fontSize: 18, color: dk.textMuted }} />
                                                            <Typography variant="body2" sx={{ color: dk.textSecondary }}>{user.username}</Typography>
                                                        </Box>
                                                    ) : (
                                                        <Typography sx={{ color: dk.textMuted }} variant="body2">—</Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {user.phone_number ? (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <PhoneIcon sx={{ fontSize: 18, color: dk.textMuted }} />
                                                            <Typography variant="body2" sx={{ color: dk.textSecondary }}>{user.phone_number}</Typography>
                                                        </Box>
                                                    ) : (
                                                        <Typography sx={{ color: dk.textMuted }} variant="body2">—</Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                        <Chip
                                                            label={user.account_status}
                                                            size="small"
                                                            sx={user.account_status === 'Active'
                                                                ? { color: '#10b981', bgcolor: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.3)', fontWeight: 600 }
                                                                : { color: dk.textSecondary, bgcolor: dk.elevated, border: `1px solid ${dk.border}` }
                                                            }
                                                        />
                                                        <Chip
                                                            label={user.verified ? '✓ Verified' : 'Unverified'}
                                                            size="small"
                                                            sx={user.verified
                                                                ? { color: '#10b981', bgcolor: 'transparent', border: '1px solid rgba(16,185,129,0.3)', fontSize: '0.62rem', height: 18 }
                                                                : { color: dk.textMuted, bgcolor: 'transparent', border: `1px solid ${dk.border}`, fontSize: '0.62rem', height: 18 }
                                                            }
                                                        />
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={user.trip_count ?? 0}
                                                        size="small"
                                                        icon={<FlightTakeoffIcon sx={{ fontSize: 14, color: `${dk.accentLight} !important` }} />}
                                                        sx={{ color: dk.textPrimary, bgcolor: dk.elevated, border: `1px solid ${dk.border}`, fontWeight: 600 }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ color: user.last_seen ? dk.textSecondary : dk.textMuted }}>
                                                        {timeAgo(user.last_seen)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ color: dk.textSecondary }}>
                                                        {fmtDate(user.created_at)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        startIcon={<LoginIcon />}
                                                        onClick={() => handleViewTrips(user)}
                                                        sx={{ bgcolor: dk.accent, '&:hover': { bgcolor: '#4f46e5' } }}
                                                    >
                                                        View Trips
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </>
            )}

            {/* User Trips Dialog */}
            <Dialog
                open={userTripsDialogOpen}
                onClose={() => setUserTripsDialogOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { bgcolor: dk.card, border: `1px solid ${dk.border}`, borderRadius: 2, boxShadow: '0 25px 50px rgba(0,0,0,0.5)' } }}
            >
                <DialogTitle sx={{ color: dk.textPrimary, borderBottom: `1px solid ${dk.border}`, fontWeight: 700 }}>
                    Trips for {selectedUser?.display_name}
                    {selectedUser && (
                        <Typography variant="caption" sx={{ display: 'block', color: dk.textMuted, fontWeight: 400, mt: 0.5 }}>
                            {selectedUser.email || selectedUser.username || selectedUser.phone_number || '—'} · Last active {timeAgo(selectedUser.last_seen)} · Joined {fmtDate(selectedUser.created_at)}
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent sx={{ pt: 2, bgcolor: dk.card }}>
                    {tripsError && (
                        <Alert severity="error" sx={{ mb: 2, bgcolor: 'rgba(239,68,68,0.1)', color: '#f87171', border: '1px solid rgba(239,68,68,0.3)', '& .MuiAlert-icon': { color: '#f87171' } }}>
                            {tripsError}
                        </Alert>
                    )}

                    {loadingTrips ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress sx={{ color: dk.accent }} />
                        </Box>
                    ) : userTrips.length === 0 ? (
                        <Typography sx={{ color: dk.textSecondary, py: 3 }}>
                            No trips found for this user.
                        </Typography>
                    ) : (
                        <Box>
                            {userTrips.map((trip) => {
                                const isOpen = expandedTripId === trip.trip_id;
                                const details = tripDetails[trip.trip_id];
                                return (
                                    <Box key={trip.trip_id} sx={{ mb: 1.5, borderRadius: 1, border: `1px solid ${dk.border}`, overflow: 'hidden' }}>
                                        {/* Header row */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, '&:hover': { bgcolor: dk.elevated } }}>
                                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                                <Typography sx={{ color: dk.textPrimary, fontWeight: 600 }}>{trip.trip_title}</Typography>
                                                <Typography variant="caption" sx={{ color: dk.textMuted }}>
                                                    {trip.destination} · {trip.start_date ? `${fmtDate(trip.start_date)} → ${fmtDate(trip.end_date)}` : 'No dates'} · Created {fmtDate(trip.created_at)}
                                                </Typography>
                                            </Box>
                                            <Chip
                                                label={trip.trip_status}
                                                size="small"
                                                sx={{ color: dk.textSecondary, bgcolor: dk.elevated, border: `1px solid ${dk.border}`, textTransform: 'capitalize' }}
                                            />
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                endIcon={isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                onClick={() => handleToggleTripDetails(trip)}
                                                sx={{ color: dk.accentLight, borderColor: dk.border, '&:hover': { borderColor: dk.accent, bgcolor: dk.hover } }}
                                            >
                                                Details
                                            </Button>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                startIcon={<LoginIcon />}
                                                onClick={() => handleAccessTrip(trip)}
                                                sx={{ bgcolor: dk.accent, '&:hover': { bgcolor: '#4f46e5' } }}
                                            >
                                                Access
                                            </Button>
                                        </Box>

                                        {/* Inline details drill-down */}
                                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                            <Divider sx={{ borderColor: dk.border }} />
                                            <Box sx={{ p: 2, bgcolor: dk.bg }}>
                                                {details === 'loading' && (
                                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                                        <CircularProgress size={22} sx={{ color: dk.accent }} />
                                                    </Box>
                                                )}
                                                {details === 'error' && (
                                                    <Typography variant="body2" sx={{ color: '#f87171' }}>Failed to load trip details.</Typography>
                                                )}
                                                {details && details !== 'loading' && details !== 'error' && (
                                                    <>
                                                        {/* Meta chips */}
                                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                                                            {details.duration_days > 0 && (
                                                                <Chip size="small" label={`${details.duration_days} days`} sx={{ color: dk.textSecondary, bgcolor: dk.elevated, border: `1px solid ${dk.border}` }} />
                                                            )}
                                                            {details.travel_pace && (
                                                                <Chip size="small" label={`Pace: ${details.travel_pace}`} sx={{ color: dk.textSecondary, bgcolor: dk.elevated, border: `1px solid ${dk.border}` }} />
                                                            )}
                                                            {Array.isArray(details.interests) && details.interests.map((it, i) => (
                                                                <Chip key={i} size="small" label={it} sx={{ color: dk.accentLight, bgcolor: `${dk.accent}22`, border: `1px solid ${dk.border}` }} />
                                                            ))}
                                                        </Box>

                                                        {/* Activities grouped by day */}
                                                        {details.activities && Object.keys(details.activities).length > 0 ? (
                                                            Object.keys(details.activities)
                                                                .sort((a, b) => Number(a) - Number(b))
                                                                .map((day) => (
                                                                    <Box key={day} sx={{ mb: 1.5 }}>
                                                                        <Typography variant="subtitle2" sx={{ color: dk.accentLight, fontWeight: 700, mb: 0.5 }}>
                                                                            Day {day}
                                                                        </Typography>
                                                                        {details.activities[day].map((a) => (
                                                                            <Box key={a.activity_id} sx={{ display: 'flex', alignItems: 'baseline', gap: 1, pl: 1, py: 0.3 }}>
                                                                                <Typography variant="caption" sx={{ color: dk.textMuted, minWidth: 48, fontFamily: 'monospace' }}>
                                                                                    {a.start_time ? a.start_time.slice(0, 5) : '--:--'}
                                                                                </Typography>
                                                                                <Typography variant="body2" sx={{ color: dk.textPrimary, flexGrow: 1 }}>
                                                                                    {a.title}
                                                                                    {a.location && <Box component="span" sx={{ color: dk.textMuted }}> · {a.location}</Box>}
                                                                                </Typography>
                                                                                {a.type && (
                                                                                    <Chip size="small" label={a.type} sx={{ height: 18, fontSize: '0.62rem', color: dk.textSecondary, bgcolor: dk.elevated, border: `1px solid ${dk.border}` }} />
                                                                                )}
                                                                            </Box>
                                                                        ))}
                                                                    </Box>
                                                                ))
                                                        ) : (
                                                            <Typography variant="body2" sx={{ color: dk.textMuted }}>No activities in this trip yet.</Typography>
                                                        )}
                                                    </>
                                                )}
                                            </Box>
                                        </Collapse>
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ bgcolor: dk.card, borderTop: `1px solid ${dk.border}` }}>
                    <Button onClick={() => setUserTripsDialogOpen(false)} sx={{ color: dk.textSecondary }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default UserManagement;
